import { Injectable, inject } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AppAuthService, AuthStatusInfo } from '@trumpf-uxlabs/ng-app-auth';
import { Observable, catchError, filter, finalize, map, of, shareReplay, switchMap, take, tap, throwError, timeout } from 'rxjs';
import { User } from '../core/models/user.model';
import { GridLayoutV2Service } from '../core/services/grid-layout-v2.service';
import { GridLayoutService } from '../core/services/grid-layout.service';
import { HttpService } from '../core/services/http.service';
import { AuthService } from './auth.service';
import { AvatarService } from './avatar.service';

type AuthenticatedAuthState = Extract<AuthStatusInfo, { status: 'authenticated' }>;

/** Refresh slightly before expiry so the next API call never sends a stale token. */
const TOKEN_EXPIRY_SKEW_SECONDS = 60;
const TOKEN_REFRESH_TIMEOUT_MS = 20_000;

@Injectable()
export class SalesforceAuthService extends AuthService {
    private readonly appAuthService = inject(AppAuthService);
    private readonly router = inject(Router);
    private readonly avatarService = inject(AvatarService);
    private readonly httpService = inject(HttpService);
    private readonly gridLayoutService = inject(GridLayoutService);
    private readonly gridLayoutV2Service = inject(GridLayoutV2Service);

    /** Shared in-flight silent refresh so parallel API calls wait on one renew. */
    private refreshInFlight$: Observable<string> | null = null;

    constructor() {
        super();
        this.initializeAuth();
    }

    private initializeAuth(): void {
        this.appAuthService.authState$.subscribe(state => {
            if (state.status === 'authenticated') {
                this.handleAuthenticatedState(state);
            } else if (state.status === 'unauthenticated' && this.isAuthenticatedSubject.value) {
                this.handleLogout();
            }

            if (state.status !== 'pending') {
                this.initializedSubject.next(true);
            }
        });
    }

    private handleAuthenticatedState(state: AuthenticatedAuthState): void {
        const wasAuthenticated = this.isAuthenticatedSubject.value;
        this.httpService.initHeaders();

        const user = this.mapUserFromState(state);
        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);

        // Only load avatar on initial login — not on every silent token refresh
        if (!wasAuthenticated) {
            this.avatarService.loadUserAvatar(user, updatedUser => this.currentUserSubject.next(updatedUser));

            const redirectUrl = this.consumePostLoginRedirect();
            if (redirectUrl) {
                this.router.navigateByUrl(redirectUrl);
            } else if (this.router.routerState.snapshot.url.indexOf('/access-restricted') !== -1) {
                this.router.navigateByUrl('/');
            }
        }
    }

    private mapUserFromState(state: AuthenticatedAuthState): User {
        const claims = state.id;

        return {
            id: (claims['sub'] as string) || '',
            displayName: (claims['name'] as string) || (claims['preferred_username'] as string) || '',
            email: (claims['email'] as string) || '',
            familyName: (claims['family_name'] as string) || '',
            firstName: (claims['given_name'] as string) || '',
            roles: this.extractRoles(claims),
            avatarUrl: (claims['picture'] as SafeResourceUrl) || null,
        };
    }

    private handleLogout(): void {
        this.currentUserSubject.next(null);
        this.isAuthenticatedSubject.next(false);
        this.gridLayoutService.clearAllCaches();
        this.gridLayoutV2Service.clearAllCaches();
    }

    private extractRoles(claims: Record<string, unknown>): string[] {
        const roles = claims['roles'];
        return Array.isArray(roles) ? roles.map(String) : [];
    }

    private decodeJwtPayload(token: string): Record<string, unknown> | null {
        try {
            const parts = token.split('.');
            if (parts.length < 2) {
                return null;
            }

            const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
            return JSON.parse(atob(padded)) as Record<string, unknown>;
        } catch {
            return null;
        }
    }

    private isAccessTokenExpired(token: string, skewSeconds = TOKEN_EXPIRY_SKEW_SECONDS): boolean {
        const payload = this.decodeJwtPayload(token);
        const exp = payload?.['exp'];
        if (typeof exp !== 'number') {
            return false;
        }

        return Date.now() >= (exp - skewSeconds) * 1000;
    }

    /**
     * Triggers silent re-auth via the ux-labs active-session iframe and waits for a new access token.
     * Concurrent callers share the same in-flight refresh.
     */
    private renewAccessToken(previousToken: string): Observable<string> {
        if (this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        this.appAuthService.refresh();

        this.refreshInFlight$ = this.appAuthService.authState$.pipe(
            filter(
                (state): state is AuthenticatedAuthState | Extract<AuthStatusInfo, { status: 'unauthenticated' }> =>
                    (state.status === 'authenticated' && state.accessToken !== previousToken) || state.status === 'unauthenticated'
            ),
            take(1),
            timeout({ first: TOKEN_REFRESH_TIMEOUT_MS }),
            map(state => {
                if (state.status !== 'authenticated') {
                    throw new Error('Salesforce session ended during token refresh');
                }
                return state.accessToken;
            }),
            tap(() => {
                this.httpService.initHeaders();
            }),
            catchError(error => {
                console.error('Token refresh failed:', error);
                this.logout();
                return throwError(() => error);
            }),
            finalize(() => {
                this.refreshInFlight$ = null;
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        return this.refreshInFlight$;
    }

    public login(): void {
        this.appAuthService.login();
    }

    public loginPopup(): Observable<void> {
        this.login();
        return of(void 0);
    }

    public logout(): void {
        this.appAuthService.logout();
    }

    public logoutPopup(): Observable<void> {
        this.logout();
        return of(void 0);
    }

    public getAccessToken(): Observable<string> {
        return this.appAuthService.authState$.pipe(
            filter((state): state is AuthenticatedAuthState => state.status === 'authenticated'),
            take(1),
            switchMap(state => {
                if (!this.isAccessTokenExpired(state.accessToken)) {
                    this.httpService.initHeaders();
                    return of(state.accessToken);
                }

                return this.renewAccessToken(state.accessToken);
            }),
            catchError(error => {
                console.error('Token acquisition failed:', error);
                return throwError(() => error);
            })
        );
    }

    public getAccessTokenWithRetry(): Observable<string> {
        return this.getAccessToken();
    }

    public checkAndRefreshToken(): Observable<boolean> {
        if (!this.isAuthenticated()) {
            return throwError(() => new Error('User not authenticated'));
        }

        return this.appAuthService.authState$.pipe(
            filter((state): state is AuthenticatedAuthState => state.status === 'authenticated'),
            take(1),
            switchMap(state => this.renewAccessToken(state.accessToken)),
            map(() => true),
            catchError(error => {
                console.error('Token refresh failed:', error);
                return throwError(() => error);
            })
        );
    }
}
